import styles from './BelotePage.module.scss';
import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { connectToGameSocket, disconnectFromSocket } from '../../modules/socketActions';
import { showGameInviteDialog, showInviteToast } from '../../modules/gameInvites';
import { GameSocketContext, GameSettingsContext } from '../../modules/socketContexts';
import { sortCards } from '../../modules/GameFunctions';
import {
    muteRemoteUser,
    startVoice,
    stopVoice,
    subscribeSpeakingUpdates,
    toggleMic,
    sendFriendRequest,
    acceptFriendRequest,
    rejectFriendRequest,
    joinGame,
    getNativeProfile,
    postNative,
    nativeInvites,
} from '../../modules/nativeBridge';
import Card from '../../components/GameComponents/Card';

// ── Game constants ─────────────────────────────────────────────────────────────
const TOTAL_TRICKS = 13;   // 52 cards / 4 players = 13 tricks per round
// Fallback only. The real value arrives with every round update as
// turnTimeoutMs, so the ring can never drift out of step with the server.
const TURN_TIMEOUT_FALLBACK_MS = 12000;

// ── Suit display helpers ───────────────────────────────────────────────────────
const SUIT_LABEL = { C: '♣', D: '♦', H: '♥', S: '♠' };
const SUIT_COLOR = { C: '#1a1a2e', D: '#c0392b', H: '#c0392b', S: '#1a1a2e' };
const SUIT_NAME  = { C: 'Trèfle', D: 'Carreau', H: 'Cœur', S: 'Pique' };

// ── Coin animations ───────────────────────────────────────────────────────────
// The wallet lives in the Flutter shell's coin bar — the paywall — which sits
// above this webview. The game never draws a balance of its own; it only shows
// coins *moving* toward that bar, and the number they land on is set natively
// from the entryFeePaid / gameOver bridge messages. PAYWALL_X/Y is where the bar
// is relative to us: top-left, just past the top edge, so the coins visibly
// leave the table and enter the wallet.
const PAYWALL_X = 56;
const PAYWALL_Y = -26;

const CoinDeltaBadge = ({ amount }) => {
    if (!amount) return null;
    const gain = amount > 0;
    return (
        <div className={`${styles.coinDelta} ${gain ? styles.coinDeltaPlus : styles.coinDeltaMinus}`}>
            {gain ? '+' : '−'}{Math.abs(amount)}
        </div>
    );
};

const FlyingCoins = ({ active, fromX, fromY, toX = PAYWALL_X, toY = PAYWALL_Y, count = 10 }) => {
    if (!active) return null;
    const coins = Array.from({ length: count }, (_, i) => {
        const mx = (Math.random() - 0.5) * 140;
        const my = (Math.random() - 0.5) * 80 - 40;
        return {
            key: i,
            style: {
                '--sx': `${fromX}px`, '--sy': `${fromY}px`,
                '--mx': `${fromX + mx}px`, '--my': `${fromY + my}px`,
                '--ex': `${toX + (Math.random() - 0.5) * 30}px`,
                '--ey': `${toY + (Math.random() - 0.5) * 20}px`,
                '--dur': `${550 + Math.random() * 150}ms`,
                '--delay': `${i * 70}ms`,
            },
        };
    });
    return (
        <div className={styles.flyingCoinContainer}>
            {coins.map(c => <div key={c.key} className={styles.flyingCoin} style={c.style} />)}
        </div>
    );
};

// ── Avatar helper ─────────────────────────────────────────────────────────────
// Kozmetik çerçeve (frameUrl): uygulamadaki FramedUserAvatar'ın karşılığı —
// avatar kutusunun 1/0.82 katı kare overlay, avatarın üstünde, ortalı.
const FRAME_STYLE = { position: 'absolute', left: '50%', top: '50%', width: '122%', height: '122%', transform: 'translate(-50%,-50%)', objectFit: 'contain', pointerEvents: 'none', borderRadius: 0 };
const Avatar = ({ profile, size = 'sm', onClick }) => {
    const url = profile?.avatarUrl;
    const frame = profile?.frameUrl;
    const name = profile?.displayName || '?';
    const cls = size === 'lg' ? styles.avatarLg : styles.avatarSm;
    return (
        <div className={cls} onClick={onClick} role={onClick ? 'button' : undefined} style={frame ? { position: 'relative', overflow: 'visible' } : undefined}>
            {url
                ? <img src={'/avatar-proxy?url=' + encodeURIComponent(url)} alt="" className={styles.avatarImg} style={frame ? { borderRadius: '50%' } : undefined} />
                : <span>{name.charAt(0).toUpperCase()}</span>}
            {frame ? <img src={frame} alt="" style={FRAME_STYLE} onError={(e) => { e.currentTarget.remove(); }} /> : null}
        </div>
    );
};

// ── Error message mapping (same as FourColors) ───────────────────────────────
const friendErrorMsg = (code) => {
    if (code === 400) return 'Requête invalide';
    if (code === 401) return 'Session expirée';
    if (code === 403) return 'Yetkiniz yok';
    if (code === 404) return 'Utilisateur introuvable';
    if (code === 409) return 'Vous êtes déjà amis ou une demande existe';
    if (code === 429) return 'Trop de demandes, patiente un peu';
    if (code === 500) return 'Erreur serveur, réessaie';
    return null;
};

// ── Profile popup ─────────────────────────────────────────────────────────────
const ProfilePopup = ({ profile, socket, onClose, onToggleMute, isMuted }) => {
    const [friendBtnText, setFriendBtnText] = useState('👤 Ajouter en ami');
    const [friendBtnDisabled, setFriendBtnDisabled] = useState(false);
    if (!profile) return null;
    const r = Math.round(profile.rating || 0);
    const interests = profile.interests || [];
    const isBot = !!profile.isBot;
    const targetCallerId = profile.callerId || profile.numericId;

    const handleFriendClick = async () => {
        if (!targetCallerId || isBot) return;
        setFriendBtnText('...');
        setFriendBtnDisabled(true);
        const result = await sendFriendRequest(socket, targetCallerId, '', profile.userId);
        if (result.success) {
            setFriendBtnText('✓ Demande envoyée');
        } else {
            const msg = (result.data && result.data.message)
                || friendErrorMsg(result.statusCode)
                || result.message
                || 'Une erreur est survenue';
            setFriendBtnText('✗ ' + msg);
            setFriendBtnDisabled(false);
        }
    };

    return (
        <div className={styles.profileOverlay} onClick={onClose}>
            <div className={styles.profileCard} onClick={e => e.stopPropagation()}>
                <Avatar profile={profile} size="lg" />
                <p className={styles.profileName}>
                    {profile.displayName}
                    {isBot && <span className={styles.botProfileBadge}>Bot</span>}
                </p>
                {isBot && (
                    <p className={styles.profileBotDesc}>Joueur automatique</p>
                )}
                {!isBot && r > 0 && (
                    <div className={styles.profileStars}>
                        {Array.from({ length: 5 }, (_, i) => (
                            <span key={i} className={i < r ? styles.starFilled : styles.starEmpty}>★</span>
                        ))}
                    </div>
                )}
                {!isBot && interests.length > 0 && (
                    <div className={styles.profileInterests}>
                        {interests.map((item, i) => (
                            <span key={i} className={styles.interestTag}>
                                {item.icon || ''} {item.name || ''}
                            </span>
                        ))}
                    </div>
                )}
                <div className={styles.profileActions}>
                    {!isBot && targetCallerId && (
                        <button
                            className={friendBtnDisabled ? styles.friendBtnDisabled : styles.friendBtn}
                            onClick={handleFriendClick}
                            disabled={friendBtnDisabled}
                        >
                            {friendBtnText}
                        </button>
                    )}
                    {!isBot && onToggleMute && (
                        <button className={styles.muteBtn} onClick={() => onToggleMute(profile)}>
                            {isMuted ? 'Activer le son' : 'Couper le son'}
                        </button>
                    )}
                </div>
                <button className={styles.profileClose} onClick={onClose}>Fermer</button>
            </div>
        </div>
    );
};

// ── Friend request overlay ────────────────────────────────────────────────────
const FriendRequestOverlay = ({ data, onAccept, onReject }) => {
    const [visible, setVisible] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => setVisible(false), 30000);
        return () => clearTimeout(timer);
    }, []);

    if (!visible || !data) return null;

    return (
        <div className={styles.friendReqBar}>
            <div className={styles.friendReqAvatar}>
                {data.fromAvatarUrl
                    ? <img src={'/avatar-proxy?url=' + encodeURIComponent(data.fromAvatarUrl)} alt="" className={styles.avatarImg} />
                    : <span>{(data.fromPlayerName || '?').charAt(0).toUpperCase()}</span>}
            </div>
            <span className={styles.friendReqText}>
                <b>{data.fromPlayerName}</b> t’a envoyé une demande d’ami
            </span>
            <button className={styles.friendReqAccept} onClick={() => { onAccept(data); setVisible(false); }}>Accepter</button>
            <button className={styles.friendReqReject} onClick={() => { onReject(data); setVisible(false); }}>Refuser</button>
        </div>
    );
};

// ── Chat panel ────────────────────────────────────────────────────────────────
const ChatPanel = ({ socket, displayName }) => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [unread, setUnread] = useState(0);
    const [input, setInput] = useState('');
    const logRef = useRef(null);

    useEffect(() => {
        if (!socket) return;
        const handler = (msg) => {
            setMessages(prev => {
                const next = [...prev, msg];
                return next.length > 30 ? next.slice(-30) : next;
            });
            // Only increment unread for other players' messages
            if (msg.playerName !== displayName) {
                setUnread(prev => prev + 1);
            }
        };
        socket.on('chat', handler);
        return () => socket.off('chat', handler);
    }, [socket, displayName]);

    useEffect(() => {
        if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
    }, [messages]);

    useEffect(() => {
        if (open) setUnread(0);
    }, [open]);

    const handleSend = (e) => {
        e.preventDefault();
        const text = input.trim();
        if (!text || !socket) return;
        socket.emit('chat', text);
        setInput('');
    };

    // Chat panel rendered via portal to body — escapes .page stacking context
    const chatPortal = open ? createPortal(
        <>
            <div className={styles.chatOverlay} onClick={() => setOpen(false)} />
            <div className={styles.chatPanel}>
                <div className={styles.chatHeader}>
                    <h3>Chat</h3>
                    <button className={styles.chatClose} onClick={() => setOpen(false)}>×</button>
                </div>
                <div className={styles.chatLog} ref={logRef}>
                    {messages.map((m, i) => (
                        <div key={i} className={styles.chatLine}>
                            <span className={styles.chatName}>{m.playerName}:</span>
                            {m.text}
                        </div>
                    ))}
                </div>
                <form className={styles.chatForm} onSubmit={handleSend}>
                    <input
                        className={styles.chatInput}
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        maxLength={200}
                        placeholder="Écris un message..."
                        autoComplete="off"
                    />
                    <button type="submit" className={styles.chatSend}>➤</button>
                </form>
            </div>
        </>,
        document.body
    ) : null;

    return (
        <>
            {!open && (
                <button className={styles.controlBtn} onClick={() => setOpen(true)} style={{ background: 'rgba(18,34,62,0.88)', position: 'relative' }}>
                    💬
                    {unread > 0 && <span className={styles.chatBadge}>{unread}</span>}
                </button>
            )}
            {chatPortal}
        </>
    );
};

// ── Score header ───────────────────────────────────────────────────────────────
// Tekli batak: takım yok — dört oyuncu tek satırda, kişisel puan ve aldığı el.
// İhaleci ayrıca hedefiyle birlikte gösterilir (ör. 3/7).
const SoloScoreHeader = ({ gameStatus, roundStatus, status, localUsername }) => {
    const players = roundStatus?.players || gameStatus.players || [];
    const scores = gameStatus.playerScores || [0, 0, 0, 0];
    const tricks = roundStatus?.tricksByPlayer || [0, 0, 0, 0];
    const trump = roundStatus?.trump;
    const bidder = roundStatus?.highBidder;
    const highBid = roundStatus?.highBid;
    const showTricks = status === 'in_progress';

    return (
        <div className={styles.scoreHeader}>
            <div className={styles.soloRow}>
                {players.map((name, i) => {
                    const isMe = name === localUsername;
                    const isBidder = name === bidder;
                    return (
                        <div
                            key={name + i}
                            className={[styles.soloCell, isMe && styles.soloCellMe, isBidder && styles.soloCellBidder]
                                .filter(Boolean).join(' ')}
                        >
                            <span className={styles.soloName}>
                                {isBidder && <span className={styles.soloBidderTag}>★</span>}
                                {isMe ? 'Sen' : name}
                            </span>
                            <span className={[styles.soloPts, (scores[i] || 0) < 0 && styles.negScore]
                                .filter(Boolean).join(' ')}>
                                {scores[i] || 0}
                            </span>
                            {showTricks && (
                                <span className={styles.soloTricks}>
                                    {tricks[i] || 0}{isBidder && highBid ? `/${highBid}` : ''} lev.
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
            {trump && (
                <div className={styles.soloTrumpRow}>
                    <span className={styles.soloTrumpLabel}>Atout</span>
                    <span className={styles.trumpDisplay} style={{ color: SUIT_COLOR[trump] }}>
                        {SUIT_LABEL[trump]}
                    </span>
                    {roundStatus?.trumpBreakRule && !roundStatus?.trumpBroken && (
                        <span className={styles.soloTrumpClosed}>· atout non coupé</span>
                    )}
                </div>
            )}
        </div>
    );
};

// Takımsız modlar: tekli ve gömmeli — puan kişiseldir.
const INDIVIDUAL_MODES = ['solo', 'gomme'];
const isIndividualMode = (m) => INDIVIDUAL_MODES.includes(m);

const ScoreHeader = ({ gameStatus, roundStatus, status, localUsername }) => {
    if (!gameStatus) return null;
    // Tekli/gömmeli batakta takım/eş yoktur → ayrı başlık
    if (isIndividualMode(gameStatus.mode || roundStatus?.mode)) {
        return (
            <SoloScoreHeader
                gameStatus={gameStatus}
                roundStatus={roundStatus}
                status={status}
                localUsername={localUsername}
            />
        );
    }
    const trump   = roundStatus?.trump;
    const round   = (gameStatus.completedRounds || 0) + 1;
    const s0      = gameStatus.teamScores[0];
    const s1      = gameStatus.teamScores[1];
    const myTeam  = gameStatus.teams.findIndex(t => t.includes(localUsername));

    const showTricks = status === 'in_progress';
    const tw0 = roundStatus?.tricksWon?.[0] ?? 0;
    const tw1 = roundStatus?.tricksWon?.[1] ?? 0;
    const highBid   = roundStatus?.highBid;
    const bidderTeam = roundStatus?.highBidder
        ? gameStatus.teams.findIndex(t => t.includes(roundStatus.highBidder))
        : -1;

    return (
        <div className={styles.scoreHeader}>
            {/* ── Scores row ── */}
            <div className={styles.headerMain}>
                <div className={styles.teamBlock}>
                    <div className={styles.teamNames}>{gameStatus.teams[0].join(' & ')}</div>
                    <div className={[styles.teamPoints, s0 < 0 && styles.negScore, myTeam === 0 && styles.myTeamPts].filter(Boolean).join(' ')}>
                        {s0}
                    </div>
                </div>
                <div className={styles.centerInfo}>
                    {/* No coin balance here on purpose — the wallet is the app's
                        paywall bar above this webview, and it is the only place
                        a balance is ever shown. */}
                    <span className={styles.roundBadge}>Manche {round}/{gameStatus.maxRounds}</span>
                    {trump && (
                        <div className={styles.trumpDisplay} style={{ color: SUIT_COLOR[trump] }}>
                            {SUIT_LABEL[trump]}
                        </div>
                    )}
                </div>
                <div className={`${styles.teamBlock} ${styles.teamBlockRight}`}>
                    <div className={styles.teamNames}>{gameStatus.teams[1].join(' & ')}</div>
                    <div className={[styles.teamPoints, s1 < 0 && styles.negScore, myTeam === 1 && styles.myTeamPts].filter(Boolean).join(' ')}>
                        {s1}
                    </div>
                </div>
            </div>

            {/* ── row 2: live trick scoreboard ── */}
            {showTricks && (
                <div className={styles.trickBar}>
                    {/* Team 0 */}
                    <div className={`${styles.trickTeam} ${myTeam === 0 ? styles.trickMine : ''}`}>
                        <span className={styles.trickN}>{tw0}</span>
                        <span className={styles.trickSuffix}>lev.</span>
                        {bidderTeam === 0 && (
                            <span className={styles.trickTarget}>/{highBid}</span>
                        )}
                    </div>

                    {/* Progress tracks */}
                    <div className={styles.trickProgress}>
                        <div className={styles.trickTrackL}>
                            <div className={styles.trickFillL} style={{ width: `${(tw0 / TOTAL_TRICKS) * 100}%` }} />
                        </div>
                        <span className={styles.trickVs}>vs</span>
                        <div className={styles.trickTrackR}>
                            <div className={styles.trickFillR} style={{ width: `${(tw1 / TOTAL_TRICKS) * 100}%` }} />
                        </div>
                    </div>

                    {/* Team 1 */}
                    <div className={`${styles.trickTeam} ${styles.trickTeamRight} ${myTeam === 1 ? styles.trickMine : ''}`}>
                        {bidderTeam === 1 && (
                            <span className={styles.trickTarget}>/{highBid}</span>
                        )}
                        <span className={styles.trickN}>{tw1}</span>
                        <span className={styles.trickSuffix}>lev.</span>
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Bid panel ─────────────────────────────────────────────────────────────────
const BidPanel = ({ roundStatus, localUsername, validBids, onBid, onPass }) => {
    const isMyTurn = roundStatus.pTurnName === localUsername;
    const players = roundStatus.players || [];
    const profiles = roundStatus.playerProfiles || {};
    const passedSet = new Set((roundStatus.bidHistory || []).filter(b => b.bid === 'P').map(b => b.player));

    return (
        <div className={styles.centerPanel}>
            {/* Player bid status row */}
            <div className={styles.bidPlayers}>
                {players.map(name => {
                    const isTurn = roundStatus.pTurnName === name;
                    const passed = passedSet.has(name);
                    const isBidder = roundStatus.highBidder === name;
                    const profile = profiles[name] || { displayName: name };
                    return (
                        <div key={name} className={`${styles.bidPlayerChip} ${isTurn ? styles.bidPlayerActive : ''} ${passed ? styles.bidPlayerPassed : ''}`}>
                            <Avatar profile={profile} size="sm" />
                            <span className={styles.bidPlayerName}>{name === localUsername ? 'Sen' : name}</span>
                            {passed && <span className={styles.bidPlayerStatus}>Passe</span>}
                            {isBidder && <span className={styles.bidPlayerBid}>{roundStatus.highBid}</span>}
                        </div>
                    );
                })}
            </div>

            {/* Current high bid */}
            <div className={styles.bidInfo}>
                {roundStatus.highBidder
                    ? <span>Meilleure enchère : <b>{roundStatus.highBid}</b> — {roundStatus.highBidder}</span>
                    : <span>Enchères ouvertes</span>
                }
            </div>

            {/* My turn actions or waiting */}
            {isMyTurn ? (
                <div>
                    <p className={styles.centerLabel}>Ton enchère :</p>
                    <div className={styles.bidButtons}>
                        {validBids.filter(b => b !== 'P').map(b => (
                            <button key={b} className={styles.bidBtn} onClick={() => onBid(b)}>{b}</button>
                        ))}
                        <button className={styles.passBtn} onClick={onPass}>Passe</button>
                    </div>
                </div>
            ) : (
                <p className={styles.waitLabel}>{roundStatus.pTurnName} enchérit...</p>
            )}
        </div>
    );
};

// ── Trump selector ────────────────────────────────────────────────────────────
const TrumpSelector = ({ roundStatus, localUsername, onSelectTrump }) => {
    const isMe = roundStatus.highBidder === localUsername;
    return (
        <div className={styles.centerPanel}>
            {isMe ? (
                <div>
                    <p className={styles.centerLabel}>{roundStatus.highBid}  — tu prends, choisis l’atout :</p>
                    <div className={styles.trumpCards}>
                        {['C', 'D', 'H', 'S'].map(s => (
                            <div
                                key={s}
                                className={styles.trumpCard}
                                style={{ '--card-color': SUIT_COLOR[s] }}
                                onClick={() => onSelectTrump(s)}
                            >
                                <div className={styles.trumpCardCorner}>
                                    <span className={styles.trumpCardSuit}>{SUIT_LABEL[s]}</span>
                                </div>
                                <div className={styles.trumpCardCenter}>{SUIT_LABEL[s]}</div>
                                <div className={styles.trumpCardName}>{SUIT_NAME[s]}</div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <p className={styles.waitLabel}>{roundStatus.highBidder} choisit l’atout...</p>
            )}
        </div>
    );
};

// ── Table (4 cards) ───────────────────────────────────────────────────────────
// Resting slot for each seat, relative to the middle of the felt. The four slots
// are spread just far enough apart that no card's top-left index is hidden by a
// neighbour, so you can still read every card that has been played. Each card
// flies in from further along its own seat direction — the pile assembles itself
// from the four sides of the table.
const SEAT_SLOT = {
    n: { x: -4,  y: -46, r: -7 },
    e: { x:  45, y:  -6, r:  7 },
    s: { x:   6, y:  46, r:  4 },
    w: { x: -45, y:   6, r: -8 },
};
const SEAT_ORDER = ['s', 'e', 'n', 'w'];        // 4 kişilik: ben güneyde
const SEAT_ORDER_3 = ['s', 'e', 'w'];           // 3 kişilik (gömmeli): kuzey boş
const seatOrderFor = (n) => (n === 3 ? SEAT_ORDER_3 : SEAT_ORDER);

const TRICK_COLLECT_MS = 560;

const TableCenter = ({ roundStatus, localUsername }) => {
    const players = roundStatus.players;
    const localIdx = players.indexOf(localUsername);
    const n = players.length || 4;
    const order = seatOrderFor(n);
    const seatOf = name => order[(players.indexOf(name) - localIdx + n) % n];

    const onTable = roundStatus.cardsOnTable || [];
    const tableKey = onTable.map(c => `${c.placedBy}${c.suit}${c.rank}`).join('|');

    // The server clears the table 1.5s after the fourth card, and the new pTurnName
    // is the trick winner. Hold the finished trick on screen for a beat longer so
    // it can be swept toward whoever took it.
    const [collect, setCollect] = useState(null);
    const prevCardsRef = useRef([]);

    useEffect(() => {
        const prev = prevCardsRef.current;
        prevCardsRef.current = onTable;
        if (prev.length === 4 && onTable.length === 0) {
            setCollect({ cards: prev, seat: seatOf(roundStatus.pTurnName) });
            const t = setTimeout(() => setCollect(null), TRICK_COLLECT_MS);
            return () => clearTimeout(t);
        }
        if (onTable.length > 0) setCollect(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableKey]);

    const shown = collect ? collect.cards : onTable;
    const sweep = collect ? SEAT_SLOT[collect.seat] : null;

    return (
        <div className={styles.tablePile}>
            {roundStatus.trump && (
                <div
                    className={`${styles.tableTrump} ${roundStatus.trumpBroken ? styles.tableTrumpBroken : ''}`}
                    style={{ color: SUIT_COLOR[roundStatus.trump] }}
                >
                    {SUIT_LABEL[roundStatus.trump]}
                </div>
            )}

            {shown.map((card, i) => {
                const seat = seatOf(card.placedBy);
                const slot = SEAT_SLOT[seat];
                // Kendi kartımız elden masaya uçarak gitsin (ortada "şak" diye
                // belirmesin): güney koltuk = yerel oyuncu, eli ekranın altında.
                const isSelf = seat === 's';
                return (
                    <div
                        key={`${card.suit}${card.rank}`}
                        className={[
                            styles.pileCard,
                            isSelf ? styles.pileCardSelf : '',
                            collect ? styles.pileCardCollect : '',
                        ].filter(Boolean).join(' ')}
                        style={{
                            zIndex: 10 + i,
                            '--x': `${slot.x}px`,
                            '--y': `${slot.y}px`,
                            '--r': `${slot.r}deg`,
                            '--sweep-x': sweep ? `${sweep.x * 3.2}px` : '0px',
                            '--sweep-y': sweep ? `${sweep.y * 3.2}px` : '0px',
                        }}
                    >
                        <Card suit={card.suit} rank={card.rank} />
                    </div>
                );
            })}
        </div>
    );
};

// ── Gömü paneli (yalnız gömmeli mod) ─────────────────────────────────────────
// İhaleyi kazanan yerdeki 4 kartı eline alır (20 kart) ve 4 kart gömer.
// Gömülenler kapalı kalır, o turda oyuna girmez.
const BuryPanel = ({ roundStatus, localUsername, selectedCount, onBury }) => {
    const isBidder = roundStatus.highBidder === localUsername;
    const need = roundStatus.buriedCount || 4;

    if (!isBidder) {
        return (
            <div className={styles.buryPanel}>
                <p className={styles.buryTitle}>
                    {roundStatus.highBidder} a pris avec {roundStatus.highBid}
                </p>
                <p className={styles.burySub}>Écart en cours, patiente…</p>
            </div>
        );
    }

    // Kartlar ekranın altındaki KENDİ elinden seçilir — 16 kart zaten oraya
    // sığıyor; ayrı bir ızgara dar ekranlarda taşıyordu.
    return (
        <div className={styles.buryPanel}>
            <p className={styles.buryTitle}>Tu prends avec {roundStatus.highBid}</p>
            <p className={styles.burySub}>
                Choisis {need} cartes à écarter · <b>{selectedCount}/{need}</b>
            </p>
            <p className={styles.buryHint}>
                Après l’écart tu choisiras l’atout ; les {need} cartes du talon passent dans ta main.
            </p>
            <button
                className={styles.buryBtn}
                disabled={selectedCount !== need}
                onClick={onBury}
            >
                ÉCARTER
            </button>
        </div>
    );
};

// ── Round score overlay ───────────────────────────────────────────────────────
const RoundScore = ({ roundScore, gameStatus }) => {
    if (!roundScore) return null;
    // Tekli/gömmeli: oyuncular kişisel puanıyla listelenir
    if (roundScore.individual || isIndividualMode(roundScore.mode)) {
        const players = gameStatus?.players || [];
        const totals = gameStatus?.playerScores || [0, 0, 0, 0];
        const rows = players.map((name, i) => ({
            name,
            idx: i,
            tricks: roundScore.tricksByPlayer?.[i] ?? 0,
            delta: roundScore.playerScores?.[i] ?? 0,
            total: totals[i] ?? 0,
            isBidder: i === roundScore.bidderIdx,
        })).sort((a, b) => b.delta - a.delta);

        return (
            <div className={styles.roundScorePanel}>
                <p className={styles.roundScoreTitle}>Manche terminée</p>
                <div className={`${styles.roundScoreBadge} ${roundScore.made ? styles.madeBadge : styles.bustBadge}`}>
                    {roundScore.made
                        ? `✓ ${roundScore.highBidder} ${roundScore.highBid} levées : contrat réussi`
                        : `✗ ${roundScore.highBidder} ${roundScore.highBid} levées : contrat chuté`}
                </div>
                <table className={styles.roundScoreTable}>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.name + r.idx}
                                className={`${styles.roundScoreTeamRow} ${r.isBidder ? styles.bidderRow : ''}`}>
                                <td className={styles.rTeamName}>
                                    {r.isBidder ? '★ ' : ''}{r.name}
                                </td>
                                <td className={styles.rTricks}>{r.tricks} lev.</td>
                                <td className={`${styles.rDelta} ${r.delta >= 0 ? styles.plus : styles.minus}`}>
                                    {r.delta >= 0 ? `+${r.delta}` : r.delta}
                                </td>
                                <td className={styles.rTotal}>{r.total}p</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <p className={styles.roundScoreNext}>Calcul des résultats...</p>
            </div>
        );
    }

    const bt = roundScore.bidderTeam;
    const ot = 1 - bt;
    const totalAfter = gameStatus?.teamScores || [0, 0];

    const elNo = gameStatus?.completedRounds || '?';
    const maxEl = gameStatus?.maxRounds || 10;

    return (
        <div className={styles.roundScorePanel}>
            <p className={styles.roundScoreTitle}>Manche {elNo} / {maxEl} terminée</p>

            {/* Made / bust badge */}
            <div className={`${styles.roundScoreBadge} ${roundScore.made ? styles.madeBadge : styles.bustBadge}`}>
                {roundScore.made
                    ? `✓ ${roundScore.highBidder} ${roundScore.highBid} levées : contrat réussi`
                    : `✗ ${roundScore.highBidder} ${roundScore.highBid} levées : contrat chuté`}
            </div>

            <table className={styles.roundScoreTable}>
                <tbody>
                    {[bt, ot].map(ti => {
                        const delta = roundScore.scores[ti];
                        const isBidder = ti === bt;
                        return (
                            <tr key={ti} className={`${styles.roundScoreTeamRow} ${isBidder ? styles.bidderRow : ''}`}>
                                <td className={styles.rTeamName}>
                                    {gameStatus?.teams[ti]?.join(' & ')}
                                </td>
                                <td className={styles.rTricks}>
                                    {roundScore.tricksWon[ti]} levées
                                </td>
                                <td className={`${styles.rDelta} ${delta >= 0 ? styles.plus : styles.minus}`}>
                                    {delta >= 0 ? `+${delta}` : delta}
                                </td>
                                <td className={styles.rTotal}>
                                    {totalAfter[ti]}p
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <p className={styles.roundScoreNext}>Manche suivante dans quelques secondes...</p>
        </div>
    );
};

// ── Game over panel ───────────────────────────────────────────────────────────
const GameOver = ({ gameStatus, gameOverData, onLobby, localUsername }) => {
    if (!gameStatus) return null;
    const solo = isIndividualMode(gameStatus.mode);
    const wt = gameStatus.winningTeam;
    const settlement = gameOverData?.settlement;
    const credits = settlement?.credits || [];
    const profiles = gameOverData?.playerProfiles || {};

    // Tekli batak: kazanan = en yüksek kişisel puan; sıralama puana göre.
    const soloSeats = gameStatus.players || [];
    const soloScores = gameStatus.playerScores || [];
    const scoreOf = (name) => {
        const i = soloSeats.indexOf(name);
        return i >= 0 ? (soloScores[i] ?? 0) : 0;
    };
    // Tekli batakta kazanan = en yüksek puan. İhaleyi almış olmak gerekmez ve
    // "beraberlik" gösterilmez: eşit puanlılar birlikte birinci sayılır.
    const soloTopScore = solo && soloScores.length ? Math.max(...soloScores) : null;
    const soloWinnerNames = solo
        ? soloSeats.filter((_, i) => soloScores[i] === soloTopScore)
        : [];
    const soloWinnerName = soloWinnerNames.join(' & ') || null;

    // Build sorted player list: winning team (or highest score) first
    const allPlayers = gameOverData?.players || [];
    const sorted = [...allPlayers].sort((a, b) => {
        if (solo) return scoreOf(b.displayName) - scoreOf(a.displayName);
        const aTeam = gameStatus.teams.findIndex(t => t.includes(a.displayName));
        const bTeam = gameStatus.teams.findIndex(t => t.includes(b.displayName));
        if (wt >= 0) {
            if (aTeam === wt && bTeam !== wt) return -1;
            if (bTeam === wt && aTeam !== wt) return 1;
        }
        return 0;
    });

    const getCredit = (p) => {
        if (!credits.length || !p.callerId) return null;
        const c = credits.find(c => String(c.callerId) === String(p.callerId));
        return c ? c.amount : null;
    };

    // Eşli: iki kişi birlikte kazanır → 🥇🥈🥈🏅. Tekli: bireysel sıralama.
    const MEDAL = solo ? ['🥇', '🥈', '🥉', '4️⃣'] : ['🥇', '🥈', '🥈', '🏅'];

    return (
        <div className={styles.gameOverPanel}>
            <h2 className={styles.gameOverTitle}>Partie terminée</h2>
            {solo ? (
                <p className={styles.gameOverWinner}>
                    {soloWinnerName ? `${soloWinnerName} a gagné !` : 'Partie terminée'}
                </p>
            ) : wt === -1 ? (
                <p className={styles.gameOverWinner}>Beraberlik!</p>
            ) : (
                <p className={styles.gameOverWinner}>
                    {gameStatus.teams[wt]?.join(' & ')} a gagné !
                </p>
            )}

            {/* Player rankings */}
            <div className={styles.rankingList}>
                {sorted.map((p, i) => {
                    const pTeam = gameStatus.teams.findIndex(t => t.includes(p.displayName));
                    const isWinner = solo
                        ? soloWinnerNames.includes(p.displayName)
                        : (pTeam === wt && wt >= 0);
                    const credit = getCredit(p);
                    const isMe = p.displayName === localUsername;
                    return (
                        <div key={p.userId} className={`${styles.rankRow} ${isWinner ? styles.rankRowWin : styles.rankRowLose} ${isMe ? styles.rankRowMe : ''}`}>
                            <span className={styles.rankMedal}>{MEDAL[i] || ''}</span>
                            <Avatar profile={profiles[p.displayName] || p} size="sm" />
                            <span className={styles.rankName}>
                                {p.displayName}
                                {p.isBot && <span className={styles.rankBot}>Bot</span>}
                            </span>
                            <span className={styles.rankScore}>
                                {solo ? scoreOf(p.displayName) : gameStatus.teamScores[pTeam]}p
                            </span>
                            {credit != null && (
                                <span className={`${styles.rankCoin} ${credit >= 0 ? styles.plus : styles.minus}`}>
                                    <img src="/images/coin.png" alt="" className={styles.rankCoinIcon} />
                                    {credit >= 0 ? '+' : ''}{credit}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>

            <button className={styles.lobbyBtn} onClick={onLobby}>Nouvelle partie</button>
        </div>
    );
};

// ── Countdown ring ───────────────────────────────────────────────────────────
const RING_R = 20;
const RING_C = 2 * Math.PI * RING_R;

// The ring drains clockwise and shifts green → amber → red, so the seat whose
// turn it is reads as "filling up" from across the table.
//
// It runs entirely in CSS. Driving it from React state meant a setInterval tick
// re-rendering the whole page — header, four seats, table and thirteen cards —
// twelve times a second, which is what made the first tap on a card feel like it
// stuck. Here the element is keyed on the turn, so it remounts once per turn and
// the animation plays itself; a negative delay fast-forwards it to wherever the
// server says the clock already is.
const TurnRing = ({ remainingMs, totalMs }) => {
    const elapsed = Math.max(0, totalMs - remainingMs);
    return (
        <svg className={styles.turnRing} viewBox="0 0 44 44">
            <circle cx="22" cy="22" r={RING_R} fill="none" stroke="rgba(0,0,0,0.4)" strokeWidth="3.4" />
            <circle
                className={styles.turnRingArc}
                cx="22" cy="22" r={RING_R}
                fill="none" strokeWidth="3.4"
                strokeDasharray={RING_C}
                strokeLinecap="round"
                transform="rotate(-90 22 22)"
                style={{
                    '--ring-c': `${RING_C}px`,
                    animationDuration: `${totalMs}ms`,
                    animationDelay: `-${elapsed}ms`,
                }}
            />
        </svg>
    );
};

// Only this span re-renders on the countdown tick, not the page.
const TurnSeconds = ({ remainingMs, totalMs }) => {
    const [secs, setSecs] = useState(() => Math.max(0, Math.ceil(remainingMs / 1000)));

    useEffect(() => {
        const endAt = Date.now() + remainingMs;
        setSecs(Math.max(0, Math.ceil(remainingMs / 1000)));
        const t = setInterval(() => {
            const s = Math.max(0, Math.ceil((endAt - Date.now()) / 1000));
            setSecs(s);
            if (s <= 0) clearInterval(t);
        }, 250);
        return () => clearInterval(t);
    }, [remainingMs]);

    const total = Math.max(1, Math.round(totalMs / 1000));
    const color = secs > total * 0.5 ? '#2ecc71' : secs > total * 0.25 ? '#f1c40f' : '#e74c3c';
    return <span className={styles.seatTimer} style={{ '--timer-color': color }}>{secs}</span>;
};

// ── Player label ──────────────────────────────────────────────────────────────
const PlayerLabel = ({
    name,
    isTurn,
    turnRemainingMs,
    turnTotalMs,
    turnKey,
    compact,
    isDealer,
    isBidder,
    bid,
    trump,
    isOpenPartner,
    profile,
    isDisconnected,
    isSpeaking,
    isMuted,
    onToggleMute,
    onAvatarClick,
}) => {
    const showClock = isTurn && turnRemainingMs != null && turnRemainingMs > 0;

    return (
        <div
            className={[
                styles.playerLabel,
                isTurn ? styles.activeTurn : '',
                isDisconnected ? styles.disconnectedLabel : '',
                isSpeaking ? styles.speakingLabel : '',
            ].filter(Boolean).join(' ')}
        >
            <div className={styles.avatarWrap}>
                <Avatar
                    profile={profile}
                    size="sm"
                    onClick={onAvatarClick ? () => onAvatarClick(profile) : undefined}
                />
                {/* keyed on the turn so each new turn restarts the CSS animation */}
                {showClock && <TurnRing key={turnKey} remainingMs={turnRemainingMs} totalMs={turnTotalMs} />}
                {showClock && <TurnSeconds key={`s${turnKey}`} remainingMs={turnRemainingMs} totalMs={turnTotalMs} />}
                {!profile?.isBot && onToggleMute && (
                    <button
                        type="button"
                        className={[
                            styles.micOverlay,
                            isMuted ? styles.micOverlayMuted : '',
                        ].filter(Boolean).join(' ')}
                        onClick={(event) => {
                            event.stopPropagation();
                            onToggleMute(profile);
                        }}
                    >
                        <span className={styles.micGlyph} />
                    </button>
                )}
            </div>

            {!compact && <span className={styles.seatName}>{name}</span>}

            {(isDealer || (isBidder && bid) || isOpenPartner || isDisconnected) && (
                <div className={styles.seatBadges}>
                    {isDealer && <span className={styles.badge}>D</span>}
                    {isBidder && bid && <span className={styles.badge}>{bid}{trump ? ` ${SUIT_LABEL[trump]}` : ''}</span>}
                    {isOpenPartner && <span className={styles.partnerBadge}>Partenaire</span>}
                    {isDisconnected && <span className={styles.dcBadge}>⚡</span>}
                </div>
            )}
        </div>
    );
};

// ── Main game page ─────────────────────────────────────────────────────────────
const BatakPage = () => {
    const [displayName, , nativeProfile] = useContext(GameSettingsContext);
    const [gameSocketID] = useContext(GameSocketContext);
    const navigate = useNavigate();
    const { roomId } = useParams();
    // ?spectate=1 → watch only: no seat, no entry fee, no hand. Chat and voice
    // stay on, which is the whole point of letting people drop in.
    const isSpectator = new URLSearchParams(useLocation().search).get('spectate') === '1';

    const [socket, setSocket] = useState(null);
    const [gameStatus, setGameStatus] = useState(null);
    const [roundStatus, setRoundStatus] = useState(null);
    const [playerHand, setPlayerHand] = useState([]);
    // Gömmeli: koz sonrası yerden (gömüden) gelen kartlar — elde işaretlenir
    const [newCards, setNewCards] = useState([]);
    // Gömmeli mod: gömme aşamasında elden seçilen kartlar
    const [burySelected, setBurySelected] = useState([]);
    const [validBids, setValidBids] = useState([]);
    const [validCards, setValidCards] = useState([]);
    const [roundScore, setRoundScore] = useState(null);
    const [allPassed, setAllPassed] = useState(false);
    const [micOn, setMicOn] = useState(true);
    const [mutedUsers, setMutedUsers] = useState(() => new Set());
    const [speakingUsers, setSpeakingUsers] = useState(() => new Set());
    const [profilePopup, setProfilePopup] = useState(null);
    const [friendReqData, setFriendReqData] = useState(null);
    const [coinDelta, setCoinDelta] = useState(null);
    const [flyCoins, setFlyCoins] = useState(false);
    const [gameOverData, setGameOverData] = useState(null);
    const [disconnectedUsers, setDisconnectedUsers] = useState(() => new Set());
    const [toast, setToast] = useState(null);
    const paidHandsRef = useRef(new Set());
    const trumpBrokenRef = useRef(false);

    useEffect(() => subscribeSpeakingUpdates(setSpeakingUsers), []);

    // "Koz kırıldı" duyurusu YALNIZ koz kırma kuralının geçerli olduğu modda
    // anlamlıdır. EŞLİ batakta koz ilk elden oynanabildiği için kural yok →
    // duyuru da yok. TEKLİ (solo) mod eklendiğinde sunucu round'a bir mod
    // bayrağı koyacak ve burası `roundStatus.trumpBreakRule` ile açılacak.
    // Kural yalnız TEKLİ modda geçerli; sunucu roundStatus.trumpBreakRule ile
    // bildirir (eşli modda false → duyuru yok).
    const trumpBreakRule = !!roundStatus?.trumpBreakRule;
    useEffect(() => {
        if (!trumpBreakRule) return;
        const broken = !!roundStatus?.trumpBroken;
        if (broken && !trumpBrokenRef.current) {
            const suit = roundStatus?.trump;
            setToast({ text: `Atout coupé${suit ? ` — ${SUIT_LABEL[suit]}` : ''}`, suit });
            const t = setTimeout(() => setToast(null), 2600);
            trumpBrokenRef.current = true;
            return () => clearTimeout(t);
        }
        // A fresh deal (no trump yet, nothing broken) rearms the announcement.
        if (!roundStatus?.trump) trumpBrokenRef.current = false;
    }, [roundStatus?.trumpBroken, roundStatus?.trump, trumpBreakRule]);

    // Show a −N / +N badge that flies up to the paywall bar, then clear it.
    const showCoinDelta = useCallback((amount) => {
        if (!amount) return;
        setCoinDelta(amount);
        setTimeout(() => setCoinDelta(null), 2200);
    }, []);

    useEffect(() => {
        if (!roomId || !displayName) return undefined;
        startVoice(roomId);
        return () => stopVoice();
    }, [roomId, displayName]);

    useEffect(() => {
        // Reset all state for new room
        setGameStatus(null);
        setRoundStatus(null);
        setPlayerHand([]);
        setNewCards([]);
        setValidBids([]);
        setValidCards([]);
        setRoundScore(null);
        setAllPassed(false);

        if (!roomId) { navigate('/'); return; }
        if (!displayName) { navigate('/'); return; }

        const conn = connectToGameSocket(roomId, gameSocketID, displayName, nativeProfile, isSpectator);

        conn.on('gameStatusUpdate', setGameStatus);

        // ── Kart sesleri ──────────────────────────────────────────────────
        // Masaya yeni kart düştüğünde native ses çalınır: koz atıldıysa koz
        // sesi, diğer hâllerde kart sesi. Tetikleyici sunucudan gelen ORTAK
        // durum olduğu için masadaki herkes (ve izleyiciler) aynı anda duyar.
        // El toplanınca masa boşalır; sayaç sıfırlanır ki yeni el yine çalsın.
        let prevOnTable = 0;
        const playCardSfx = (data) => {
            const table = data?.cardsOnTable || [];
            if (table.length > prevOnTable) {
                const last = table[table.length - 1];
                const isTrump = last && data?.trump && last.suit === data.trump;
                postNative('playSfx', { name: isTrump ? 'koz' : 'card_take', volume: 1.0 });
            }
            prevOnTable = table.length;
        };

        conn.on('roundStatusUpdate', data => {
            playCardSfx(data);
            setRoundStatus(data);
            setRoundScore(null);
            if (data.allPassed) setAllPassed(true);
            else setAllPassed(false);
        });
        conn.on('playerHandUpdate', data => setPlayerHand(data?.cards || []));
        conn.on('playerNewCardsUpdate', data => setNewCards(Array.isArray(data) ? data : []));
        conn.on('buryHandUpdate', () => setBurySelected([]));
        conn.on('playerBidOptionsUpdate', setValidBids);
        conn.on('playerHandValidOptionsUpdate', setValidCards);
        conn.on('roundScoreUpdate', setRoundScore);
        conn.on('friendRequestReceived', setFriendReqData);
        conn.on('friendRequestResult', data => {
            if (data.accepted) {
                window._batakFriendSet?.add(data.fromUserId);
            }
        });

        // ── Gelen oyun daveti — oyun içindeyken de göster. Kabul: tam sayfa
        // yenilemeyle ana sayfaya dön (pagehide sesli odayı kapatır); ana sayfa
        // joinRoomId ile doğrudan masaya oturur ve kabul cevabını iletir.
        conn.on('gameInviteReceived', (d) => {
            // Yeni uygulama daveti kendi banner'ıyla gösterir.
            if (nativeInvites) return;
            showGameInviteDialog(d, {
                warning: isSpectator ? '' : 'Katılırsan bu oyundan çıkacaksın',
                onAccept: () => {
                    const params = new URLSearchParams();
                    if (d.roomId) params.set('joinRoomId', d.roomId);
                    params.set('inviteFromUserId', d.fromUserId || '');
                    if (d.invitationId) params.set('invitationId', d.invitationId);
                    window.location.href = '/?' + params.toString();
                },
                onReject: () => conn.emit('gameInviteResponse', {
                    targetUserId: d.fromUserId,
                    accepted: false,
                }),
            });
        });
        conn.on('gameInviteResult', (d) => {
            const who = d.fromPlayerName || 'Joueur';
            // Oyun ortasında otomatik masa değiştirme yok — sadece bilgi ver
            showInviteToast(d.accepted ? who + ' a accepté ton invitation !' : who + ' a refusé l’invitation');
        });
        conn.on('playerDisconnected', ({ userId }) => {
            setDisconnectedUsers(prev => new Set(prev).add(userId));
        });
        conn.on('playerBecameBot', ({ userId }) => {
            setDisconnectedUsers(prev => { const s = new Set(prev); s.delete(userId); return s; });
        });
        conn.on('playerReconnected', ({ userId }) => {
            setDisconnectedUsers(prev => { const s = new Set(prev); s.delete(userId); return s; });
        });
        conn.on('gameOver', data => {
            setGameOverData(data);
            // Let the shell settle the bar and show its own badge.
            postNative('gameOver', data);
        });

        // ── Coin: entry fee, once per el ──────────────────────────────────────
        // The server signals when a hand is dealt; the debit itself is ours to
        // make because only we hold the user's token. The idempotency key is
        // scoped to the hand, so a reconnect mid-el never double-charges.
        // Belt and braces: only ever act on a close for the table we are at.
        // A stray event addressed to another room must never pull the player out
        // of the game they are actually playing.
        conn.on('roomClosed', (data) => {
            if (data?.roomId && data.roomId !== roomId) return;
            stopVoice();
            navigate('/');
        });

        conn.on('entryFee', async ({ handNo }) => {
            const p = getNativeProfile();
            // Watchers hold no seat, so they are never charged.
            if (isSpectator || !p.callerId || !handNo) return;
            if (paidHandsRef.current.has(handNo)) return;
            paidHandsRef.current.add(handNo);

            const key = `batak_${p.callerId}_${roomId}_h${handNo}`;
            const result = await joinGame(key);

            // Out of coins → the seat cannot be played. Tell the server to hand
            // it to a bot, open the shell's paywall, and leave the table.
            const msg = String(result?.data?.message || result?.data?.error || '');
            const insufficient = result?.statusCode === 402
                || /coin|bakiye|balance|insufficient|yetersiz/i.test(msg);
            if (!result?.success && insufficient) {
                conn.emit('entryFeeFailed');
                postNative('onError', {
                    code: 'insufficient_coins',
                    message: 'Coins insuffisants',
                    data: result?.data || {},
                });
                stopVoice();
                try { conn.emit('leaveGame'); } catch (e) {}
                postNative('exitGame', { roomId });
                navigate('/');
                return;
            }

            if (!result?.success || result?.data?.entryCost == null) return;

            const cost = result.data.entryCost;
            postNative('entryFeePaid', {
                gameType: 'batak',
                entryCost: cost,
                balanceAfter: result.data.balanceAfter,
                replayed: !!result.data.replayed,
                roomId,
            });
            if (!result.data.replayed && cost > 0) showCoinDelta(-cost);
        });

        // ── Coin: payout for the el that just finished ────────────────────────
        conn.on('handSettlement', ({ settlement }) => {
            const p = getNativeProfile();
            const credits = settlement?.credits || [];
            const mine = credits.find(c => String(c.callerId) === String(p.callerId));
            const won = mine?.amount || 0;
            // Always tell the shell so the bar re-reads the wallet, even at 0.
            postNative('gameOver', { settlement });
            if (won <= 0) return;
            showCoinDelta(won);
            setFlyCoins(true);
            setTimeout(() => setFlyCoins(false), 2000);
        });

        setSocket(conn);

        // Uygulama kabuğu (Flutter) oyunu kapatırken de koltuk bırakılsın:
        // pagehide, WebView yok edilirken / about:blank'e giderken tetiklenir.
        // Böylece web'deki "Lobiye Dön" dışındaki çıkışlarda da koltuk anında
        // boşalır. visibilitychange BİLEREK kullanılmıyor — uygulamayı arka
        // plana almak kopma sayılır ve grace süresi devreye girmelidir.
        const onPageHide = () => { try { conn.emit('leaveGame'); } catch (e) {} };
        window.addEventListener('pagehide', onPageHide);

        return () => {
            window.removeEventListener('pagehide', onPageHide);
            disconnectFromSocket(conn);
        };
    }, [roomId, gameSocketID, displayName, nativeProfile, navigate, showCoinDelta, isSpectator]);

    useEffect(() => {
        if (gameStatus?.gameStatus === 'over') stopVoice();
    }, [gameStatus?.gameStatus]);

    const handleBid = useCallback(amount => socket?.emit('placeBid', amount), [socket]);
    const handlePass = useCallback(() => socket?.emit('passBid'), [socket]);
    const handleSelectTrump = useCallback(suit => socket?.emit('selectTrump', suit), [socket]);
    // Gömme: seçilen 4 kart sunucuya gider; el 16'ya döner ve koz seçimi açılır.
    const handleBury = useCallback(() => {
        socket?.emit('buryCards', burySelected.map(c => ({ suit: c.suit, rank: c.rank })));
        setBurySelected([]);
    }, [socket, burySelected]);

    // Gömme aşamasında elden kart seç / seçimi kaldır (en fazla buriedCount)
    const handleToggleBury = useCallback((card) => {
        if (!card) return;
        const need = roundStatus?.buriedCount || 4;
        setBurySelected(prev => {
            const k = card.suit + card.rank;
            if (prev.some(c => c.suit + c.rank === k)) return prev.filter(c => c.suit + c.rank !== k);
            if (prev.length >= need) return prev;
            return [...prev, card];
        });
    }, [roundStatus?.buriedCount]);
    const handlePlayCard = useCallback(card => {
        if (card) socket?.emit('cardPlay', { suit: card.suit, rank: card.rank });
    }, [socket]);
    const handleGoLobby = useCallback(() => {
        stopVoice();
        // Koltuğu DERHAL bırak: bu bilinçli bir çıkış, bağlantı kopması değil.
        // Sinyal gitmezse sunucu koltuğu grace süresi boyunca bize ayrı tutuyor
        // ve masadan çıkmışken başkalarının lobisinde hâlâ oturuyor görünüyoruz.
        try { socket?.emit('leaveGame'); } catch (e) {}
        if (socket) disconnectFromSocket(socket);
        navigate('/');
    }, [socket, navigate]);
    // The el is the whole match: once it is scored and paid out there is nothing
    // left to do at this table, so everyone is returned to the lobby to pick a
    // new one. The result panel gets a beat to be read first, and "Yeni Oyun"
    // skips the wait.
    useEffect(() => {
        if (gameStatus?.gameStatus !== 'over') return undefined;
        const t = setTimeout(handleGoLobby, 9000);
        return () => clearTimeout(t);
    }, [gameStatus?.gameStatus, handleGoLobby]);

    const handleStopWatching = useCallback(() => {
        socket?.emit('unwatchRoom');
        handleGoLobby();
    }, [socket, handleGoLobby]);

    const handleToggleMic = useCallback(() => {
        setMicOn(current => {
            const next = !current;
            toggleMic(next ? 'unmute' : 'mute');
            return next;
        });
    }, []);
    const handleAvatarClick = useCallback((profile) => {
        if (profile && profile.displayName !== displayName) {
            setProfilePopup(profile);
        }
    }, [displayName]);
    const handleAcceptFriendReq = useCallback((data) => {
        acceptFriendRequest(data.fromCallerId || data.fromUserId);
        if (socket) socket.emit('friendRequestResponse', {
            targetUserId: data.fromUserId,
            accepted: true,
            fromPlayerName: displayName,
        });
        setFriendReqData(null);
    }, [socket, displayName]);
    const handleRejectFriendReq = useCallback((data) => {
        rejectFriendRequest(data.fromCallerId || data.fromUserId);
        if (socket) socket.emit('friendRequestResponse', {
            targetUserId: data.fromUserId,
            accepted: false,
            fromPlayerName: displayName,
        });
        setFriendReqData(null);
    }, [socket, displayName]);
    const handleToggleRemoteMic = useCallback((profile) => {
        const targetUserId = profile?.userId;
        if (!targetUserId || targetUserId === nativeProfile?.userId || profile?.isBot) return;

        setMutedUsers(current => {
            const next = new Set(current);
            const nextMuted = !next.has(targetUserId);
            if (nextMuted) next.add(targetUserId);
            else next.delete(targetUserId);
            muteRemoteUser(targetUserId, nextMuted);
            return next;
        });
    }, [nativeProfile?.userId]);

    if (!socket || !roundStatus) {
        return (
            <div className={styles.loadingScreen}>
                <h1 className={styles.loadingTitle}>BATAK</h1>
                <div className={styles.loadingSuits}>
                    <span className={styles.loadingSuit}>♠</span>
                    <span className={styles.loadingSuit}>♥</span>
                    <span className={styles.loadingSuit}>♦</span>
                    <span className={styles.loadingSuit}>♣</span>
                </div>
                <div className={styles.loadingDots}>
                    Connexion à la partie<span>.</span><span>.</span><span>.</span>
                </div>
            </div>
        );
    }

    const players = roundStatus.players;
    // "Ben kimim?" sorusu kendi adımızla DEĞİL, sunucudaki koltuk adıyla
    // cevaplanır: bot koltuğunu devralırken ya da ad çakışmasında koltuk adı
    // kendi adımızdan farklı olabilir. Profiller userId taşıdığı için koltuğu
    // ondan buluruz; bulunamazsa kendi adımıza düşeriz.
    const mySeatName = (() => {
        const myUserId = nativeProfile?.userId;
        const profs = roundStatus.playerProfiles || {};
        if (myUserId) {
            const hit = Object.keys(profs).find(n => profs[n] && profs[n].userId === myUserId);
            if (hit) return hit;
        }
        return displayName;
    })();
    // A spectator is in no seat, so anchor the board on player 0 and let the
    // four seats fall out naturally instead of collapsing on indexOf() = -1.
    const seatIdx = players.indexOf(mySeatName);
    const localIdx = seatIdx === -1 ? 0 : seatIdx;
    const southName = isSpectator ? players[localIdx] : mySeatName;
    const seatCount = players.length || 4;
    const getAt = offset => players[(localIdx + offset + seatCount) % seatCount];
    // 3 kişilik masada kuzey koltuğu yok: karşımdaki iki oyuncu doğu ve batıda.
    const northName = seatCount === 4 ? getAt(2) : null;
    const westName = seatCount === 4 ? getAt(3) : getAt(2);
    const eastName = getAt(1);
    const getProfile = name => roundStatus.playerProfiles?.[name] || {
        userId: name,
        displayName: name,
        avatarUrl: '',
        isBot: false,
    };
    const isMuted = name => mutedUsers.has(getProfile(name).userId);
    const isSpeaking = name => {
        const profile = getProfile(name);
        return speakingUsers.has(profile.userId) || speakingUsers.has(name);
    };
    const isTurn = name => roundStatus.pTurnName === name;
    // One key per turn: when it changes the seat's ring remounts and its CSS
    // animation restarts from whatever the server says is left on the clock.
    const turnRemainingMs = roundStatus.turnRemainingMs ?? null;
    const turnTotalMs = roundStatus.turnTimeoutMs || TURN_TIMEOUT_FALLBACK_MS;
    const turnKey = `${roundStatus.pTurnName}|${roundStatus.status}|${roundStatus.trickCount}|${(roundStatus.cardsOnTable || []).length}`;
    const isDisconnected = name => {
        const p = getProfile(name);
        return disconnectedUsers.has(p.userId) || p.connected === false;
    };
    const isDealer = idx => idx === roundStatus.dealerIndex;
    const bidder = roundStatus.highBidder;
    const trump = roundStatus.trump;

    const sortedHand = sortCards([...playerHand], trump);

    // Partner batak state
    const partnerOpenCards = roundStatus.partnerOpenCards || [];
    const sortedPartnerCards = sortCards([...partnerOpenCards], trump);
    const bidWinnerName = roundStatus.bidWinnerIdx != null ? players[roundStatus.bidWinnerIdx] : null;
    const partnerName = roundStatus.partnerIdx != null ? players[roundStatus.partnerIdx] : null;
    const isBidWinner = bidWinnerName === mySeatName;
    const isPartner = partnerName === mySeatName;
    const isPlayingForPartner = roundStatus.controllingPlayer === mySeatName
        && roundStatus.pTurnName !== mySeatName;
    const isPartnerAt = (name) => partnerName && name === partnerName && name !== mySeatName;

    // Which chair the open hand belongs to, seen from this player. 's' means the
    // open hand is our own, and our own hand already shows it — nothing extra to
    // draw. SEAT_ORDER is the same mapping the table pile uses.
    const OPEN_SEAT_CLASS = {
        n: styles.openHandSeatN,
        e: styles.openHandSeatE,
        w: styles.openHandSeatW,
    };
    const partnerSeat = roundStatus.partnerIdx != null
        ? seatOrderFor(seatCount)[(roundStatus.partnerIdx - localIdx + seatCount) % seatCount]
        : null;
    const openHandSeat = sortedPartnerCards.length > 0 && partnerSeat && partnerSeat !== 's'
        ? partnerSeat
        : null;

    // Table center content
    let tableContent = null;
    const { status } = roundStatus;
    if (gameStatus?.gameStatus === 'over') {
        tableContent = <GameOver gameStatus={gameStatus} gameOverData={gameOverData} onLobby={handleGoLobby} localUsername={mySeatName} />;
    } else if (status === 'over' && roundScore) {
        tableContent = <RoundScore roundScore={roundScore} gameStatus={gameStatus} />;
    } else if (status === 'over' && allPassed) {
        tableContent = (
            <div className={styles.allPassedPanel}>
                <p className={styles.allPassedText}>Tout le monde a passé</p>
                <p className={styles.allPassedSub}>Nouvelle distribution...</p>
            </div>
        );
    } else if (status === 'bidding') {
        tableContent = (
            <BidPanel
                roundStatus={roundStatus}
                localUsername={mySeatName}
                validBids={validBids}
                onBid={handleBid}
                onPass={handlePass}
            />
        );
    } else if (status === 'burying') {
        tableContent = (
            <BuryPanel
                roundStatus={roundStatus}
                localUsername={mySeatName}
                selectedCount={burySelected.length}
                onBury={handleBury}
            />
        );
    } else if (status === 'selecting_trump') {
        tableContent = (
            <TrumpSelector
                roundStatus={roundStatus}
                localUsername={mySeatName}
                onSelectTrump={handleSelectTrump}
            />
        );
    } else {
        tableContent = <TableCenter roundStatus={roundStatus} localUsername={mySeatName} />;
    }

    return (
        <div className={styles.page}>
            {/* Center-screen announcements (trump broken, …) */}
            {toast && (
                <div className={styles.toast} style={toast.suit ? { '--toast-suit': SUIT_COLOR[toast.suit] } : undefined}>
                    {toast.text}
                </div>
            )}

            {/* Profile popup */}
            {profilePopup && (
                <ProfilePopup
                    profile={profilePopup}
                    socket={socket}
                    onClose={() => setProfilePopup(null)}
                    onToggleMute={handleToggleRemoteMic}
                    isMuted={isMuted(profilePopup.displayName)}
                />
            )}

            {/* Friend request overlay */}
            {friendReqData && (
                <FriendRequestOverlay
                    data={friendReqData}
                    onAccept={handleAcceptFriendReq}
                    onReject={handleRejectFriendReq}
                />
            )}

            {/* Coin animations */}
            {/* Coin motion is one-way: from the table up into the app's paywall bar. */}
            <CoinDeltaBadge amount={coinDelta} />
            <FlyingCoins
                active={flyCoins}
                fromX={window.innerWidth / 2} fromY={window.innerHeight / 2}
                count={12}
            />

            {/* Chat and mic live in the top band, at the left end of the row that
                already carries the app's coin bar and exit button. At the bottom
                they sat on the cards. */}
            <div className={styles.topControls}>
                <ChatPanel socket={socket} displayName={displayName} />
                <button
                    type="button"
                    className={[
                        styles.controlBtn,
                        micOn ? styles.micButtonOn : styles.micButtonOff,
                    ].filter(Boolean).join(' ')}
                    onClick={handleToggleMic}
                >
                    <span className={styles.micGlyph} />
                </button>
            </div>

            <ScoreHeader
                gameStatus={gameStatus}
                roundStatus={roundStatus}
                status={status}
                localUsername={mySeatName}
            />

            <div className={styles.board}>
                {/* Open cards belong to whoever is showing them, so they are
                    drawn at that player's seat instead of in one shared strip
                    at the top. Side seats get them turned sideways, the way the
                    cards would actually lie on the table in front of them. */}
                {openHandSeat && (
                    <div className={`${styles.openHandSeat} ${OPEN_SEAT_CLASS[openHandSeat]}`}>
                        {/* Horizontal nameplate for the player showing their
                            cards. It carries the avatar, the turn ring and the
                            name, so this seat needs no separate chip — and it
                            sits on top of the cards it belongs to. */}
                        <div className={styles.openHandOwner}>
                            <div className={styles.avatarWrap}>
                                <Avatar
                                    profile={getProfile(partnerName)}
                                    size="sm"
                                    onClick={() => handleAvatarClick(getProfile(partnerName))}
                                />
                                {isTurn(partnerName) && turnRemainingMs > 0 && (
                                    <>
                                        <TurnRing key={turnKey} remainingMs={turnRemainingMs} totalMs={turnTotalMs} />
                                        <TurnSeconds key={`s${turnKey}`} remainingMs={turnRemainingMs} totalMs={turnTotalMs} />
                                    </>
                                )}
                            </div>
                            <span className={styles.openHandOwnerName}>{partnerName}</span>
                        </div>
                        <div className={styles.openHandRot}>
                            <PlayerHand
                                singleRow={openHandSeat !== 'n'}
                                cards={sortedPartnerCards}
                                validCards={isBidWinner && isPlayingForPartner ? validCards : []}
                                status={status}
                                onPlay={isBidWinner ? handlePlayCard : () => {}}
                                trump={trump}
                                trumpBroken={roundStatus.trumpBroken}
                            />
                        </div>
                    </div>
                )}

                {/* North — 3 kişilik gömmeli masada kuzey koltuğu yoktur */}
                <div className={styles.northZone}>
                    {northName && openHandSeat !== 'n' && <PlayerLabel
                        name={northName} isTurn={isTurn(northName)}
                        turnRemainingMs={isTurn(northName) ? turnRemainingMs : null}
                        turnTotalMs={turnTotalMs}
                        turnKey={turnKey}
                        isDealer={isDealer((localIdx + 2) % seatCount)}
                        isBidder={bidder === northName} bid={roundStatus.highBid} trump={trump}
                        isOpenPartner={isPartnerAt(northName)}
                        profile={getProfile(northName)}
                        isDisconnected={isDisconnected(northName)}
                        isSpeaking={isSpeaking(northName)}
                        isMuted={isMuted(northName)}
                        onToggleMute={handleToggleRemoteMic}
                        onAvatarClick={handleAvatarClick}
                    />}
                </div>

                {/* West */}
                <div className={`${styles.westZone} ${openHandSeat === 'w' ? styles.zoneWithOpenHand : ''}`}>
                    {openHandSeat !== 'w' && <PlayerLabel
                        name={westName} isTurn={isTurn(westName)}
                        turnRemainingMs={isTurn(westName) ? turnRemainingMs : null}
                        turnTotalMs={turnTotalMs}
                        turnKey={turnKey}
                        isDealer={isDealer((localIdx + (seatCount === 4 ? 3 : 2)) % seatCount)}
                        isBidder={bidder === westName} bid={roundStatus.highBid} trump={trump}
                        isOpenPartner={isPartnerAt(westName)}
                        profile={getProfile(westName)}
                        isDisconnected={isDisconnected(westName)}
                        isSpeaking={isSpeaking(westName)}
                        isMuted={isMuted(westName)}
                        onToggleMute={handleToggleRemoteMic}
                        onAvatarClick={handleAvatarClick}
                    />}
                </div>

                {/* Center oval table */}
                <div className={`${styles.centerZone} ${openHandSeat === 'n' ? styles.centerZoneShifted : ''}`}>
                    {tableContent}
                </div>

                {/* East */}
                <div className={`${styles.eastZone} ${openHandSeat === 'e' ? styles.zoneWithOpenHand : ''}`}>
                    {openHandSeat !== 'e' && <PlayerLabel
                        name={eastName} isTurn={isTurn(eastName)}
                        turnRemainingMs={isTurn(eastName) ? turnRemainingMs : null}
                        turnTotalMs={turnTotalMs}
                        turnKey={turnKey}
                        isDealer={isDealer((localIdx + 1) % seatCount)}
                        isBidder={bidder === eastName} bid={roundStatus.highBid} trump={trump}
                        isOpenPartner={isPartnerAt(eastName)}
                        profile={getProfile(eastName)}
                        isDisconnected={isDisconnected(eastName)}
                        isSpeaking={isSpeaking(eastName)}
                        isMuted={isMuted(eastName)}
                        onToggleMute={handleToggleRemoteMic}
                        onAvatarClick={handleAvatarClick}
                    />}
                </div>

                {/* South (local player) + controls */}
                <div className={styles.southZone}>
                    <PlayerLabel
                        name={southName} isTurn={isTurn(southName)}
                        turnRemainingMs={isTurn(southName) ? turnRemainingMs : null}
                        turnTotalMs={turnTotalMs}
                        turnKey={turnKey}
                        isDealer={isDealer(localIdx)}
                        isBidder={bidder === southName} bid={roundStatus.highBid} trump={trump}
                        isOpenPartner={isPartner}
                        profile={getProfile(southName)}
                        isSpeaking={isSpeaking(southName)}
                        onAvatarClick={isSpectator ? handleAvatarClick : undefined}
                    />
                </div>
            </div>

            {/* Spectator: no hand to show — just say so and offer the way out. */}
            {isSpectator ? (
                <div className={styles.spectatorBar}>
                    <span className={styles.spectatorTag}>👁 Tu es spectateur</span>
                    <button className={styles.unwatchBtn} onClick={handleStopWatching}>
                        Quitter le mode spectateur
                    </button>
                </div>
            ) : (
                /* Player hand — hide during trump selection */
                /* The hand stays up during trump selection: you cannot pick a
                   trump sensibly without seeing which suit you are long in. */
                <div className={styles.handArea}>
                    {/* Partner info message when local player is the partner */}
                    {isPartner && status === 'in_progress' && (
                        <div className={styles.partnerInfoMsg}>
                            Ton jeu est visible — {bidWinnerName} joue pour toi
                        </div>
                    )}

                    {/* Own hand — gömme aşamasında bu el seçim ekranıdır */}
                    <div className={isBidWinner && isPlayingForPartner ? styles.handDimmed : ''}>
                        <PlayerHand
                            cards={sortedHand}
                            validCards={isPlayingForPartner ? [] : validCards}
                            status={status}
                            onPlay={handlePlayCard}
                            trump={trump}
                            trumpBroken={roundStatus.trumpBroken}
                            buryMode={status === 'burying' && roundStatus.highBidder === mySeatName}
                            burySelected={burySelected}
                            newCards={status === 'in_progress' ? newCards : []}
                            onToggleBury={handleToggleBury}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Player hand component ──────────────────────────────────────────────────────
const isWideShort = () =>
    typeof window !== 'undefined' && window.innerWidth >= 640 && window.innerHeight <= 620;

const PlayerHand = ({ cards, validCards, status, onPlay, trump, trumpBroken, singleRow,
                     buryMode = false, burySelected = [], onToggleBury, newCards = [] }) => {
    const [selected, setSelected] = useState(-1);
    const [wideShort, setWideShort] = useState(isWideShort);

    useEffect(() => {
        const onResize = () => setWideShort(isWideShort());
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('orientationchange', onResize);
        };
    }, []);
    // Identity of the card currently on its way to the table. Held only until the
    // server sends the new hand, and it also swallows a second tap so you can't
    // fire two cards into one trick.
    const [playing, setPlaying] = useState(null);
    const canPlay = status === 'in_progress';
    const isTrumpLocked = (card) => trump && !trumpBroken && card.suit === trump;

    const isValid = useCallback(
        (card) => validCards.some(c => c.suit === card.suit && c.rank === card.rank),
        [validCards]
    );

    const handleCardClick = useCallback((idx) => {
        // Gömme aşaması: tıklama kart OYNAMAZ, seçer/seçimi kaldırır
        if (buryMode) { if (onToggleBury) onToggleBury(cards[idx]); return; }
        if (!canPlay || playing || !isValid(cards[idx])) return;
        // Single tap to play directly
        setPlaying(`${cards[idx].suit}${cards[idx].rank}`);
        onPlay(cards[idx]);
        setSelected(-1);
    }, [canPlay, cards, isValid, onPlay, playing, buryMode, onToggleBury]);

    useEffect(() => {
        const handler = (e) => {
            if (e.repeat) return;
            if ((e.code.startsWith('Digit') || e.code.startsWith('Numpad')) && canPlay) {
                const d = parseInt(e.key);
                if (d >= 1 && d <= cards.length && isValid(cards[d - 1]))
                    setSelected(prev => prev === d - 1 ? -1 : d - 1);
            }
            if ((e.code === 'Enter' || e.code === 'Space') && selected !== -1 && cards[selected]) {
                onPlay(cards[selected]);
                setSelected(-1);
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [canPlay, cards, isValid, selected, onPlay]);

    useEffect(() => { setSelected(-1); setPlaying(null); }, [cards.length, validCards]);

    const n = cards.length;

    // posInRow / rowLen fan the row into a shallow arc: cards tilt away from the
    // row's centre and the outer ones sit slightly lower, like a held hand.
    // z-index is row-local — each row is its own stacking context, so the bottom
    // row always draws in front of the top one.
    // Kavis derinliği offset² ile büyür: 16 kartlık TEK satırda uçtaki kart ~18px
    // aşağı kayıp ekranın altından taşıyordu. Uzun satırda yelpaze düzleşir.
    const renderCard = (card, globalIdx, posInRow, rowLen) => {
        const buryPicked = buryMode && burySelected.some(c => c.suit === card.suit && c.rank === card.rank);
        const isSel = selected === globalIdx || buryPicked;
        const locked = isTrumpLocked(card) && !buryMode;
        const playable = buryMode ? true : (canPlay && isValid(card));
        const isFlying = playing === `${card.suit}${card.rank}`;
        const isNew = newCards.some(c => c.suit === card.suit && String(c.rank) === String(card.rank));
        const offset = posInRow - (rowLen - 1) / 2;
        return (
            <div
                key={`${card.suit}${card.rank}`}
                className={`${styles.cardSlot} ${isSel ? styles.cardSlotSelected : ''} ${playable ? styles.cardSlotValid : ''} ${locked ? styles.cardSlotLocked : ''} ${isFlying ? styles.cardSlotFlying : ''} ${isNew ? styles.cardSlotNew : ''}`}
                style={{
                    zIndex: posInRow,
                    '--rot': `${(offset * (rowLen >= 14 ? 0.9 : rowLen >= 7 ? 1.6 : 2.5)).toFixed(2)}deg`,
                    '--arc-lift': `${(offset * offset * (rowLen >= 14 ? 0.11 : rowLen >= 7 ? 0.32 : 0.55)).toFixed(2)}px`,
                }}
                onClick={() => handleCardClick(globalIdx)}
            >
                <Card
                    suit={card.suit}
                    rank={card.rank}
                    index={globalIdx}
                    active={playable}
                    selected={isSel}
                    dim={canPlay && !playable}
                />
                {isNew && <span className={styles.newCardTag}>NEUF</span>}
            </div>
        );
    };

    // Side seats lay their open hand out in one long line: two stacked rows are
    // twice as deep, and depth is exactly what is scarce along the edge.
    // 14+ kart (gömmeli batak 16 kart) iki satırda dikeyde taşıyordu: kartlar
    // küçülür, bindirme sıkılaşır, satırlar birbirine daha çok biner.
    const denseClass = n >= 14 ? ` ${styles.handDense}` : '';

    // Yatay ekranda (geniş + kısa) kalabalık eli TEK satıra diz: orada darboğaz
    // dikey alan, yatay alan ise bol. Dar/dik ekranda iki satır kalır.
    const forceSingleRow = n >= 14 && wideShort;

    if (n > 7 && !singleRow && !forceSingleRow) {
        const mid = Math.ceil(n / 2);
        const upper = cards.slice(0, mid);
        const lower = cards.slice(mid);
        return (
            <div className={styles.handContainer + denseClass}>
                <div className={styles.handRows}>
                    <div className={styles.handRow}>
                        {upper.map((card, i) => renderCard(card, i, i, upper.length))}
                    </div>
                    <div className={`${styles.handRow} ${styles.handRowBottom}`}>
                        {lower.map((card, i) => renderCard(card, mid + i, i, lower.length))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.handContainer + denseClass}>
            <div className={styles.handRow}>
                {cards.map((card, i) => renderCard(card, i, i, n))}
            </div>
        </div>
    );
};

export default BatakPage;
